import {
  AnyAction,
  configureStore,
  EnhancedStore,
  MiddlewareArray,
} from '@reduxjs/toolkit';
import {ThunkMiddlewareFor} from '@reduxjs/toolkit/dist/getDefaultMiddleware';
import {setupListeners} from '@reduxjs/toolkit/query/react';
import {DefaultRootState} from 'react-redux';

import {throttledSaveStateToLocalStorage} from './local-storage';
import {
  APIGlobalErrorHandlerMiddleware,
  AppVersionCheckerMiddleware,
  ErrorLoggerMiddleware,
} from './middlewares';
import rootReducer from './root-reducer';
import {sweetAPI} from './sweet-api/sweet-api';

export function configureSweetStore(): {
  store: EnhancedStore<
    DefaultRootState,
    AnyAction,
    MiddlewareArray<[ThunkMiddlewareFor<DefaultRootState>]>
  >;
  stopLocalStorageSync: () => void;
} {
  const store = configureStore({
    reducer: rootReducer,

    // enable redux devtools integration on dev mode only
    devTools: process.env.NODE_ENV === 'development',

    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          // disable middleware that detects if any non-serializable values have been
          // included in state - all immutable.js objects are not serializable, so enabling
          // this covers up the devtool console with errors
          // https://redux-toolkit.js.org/api/serializabilityMiddleware
          ignoreState: true,
        },
        // disable middleware that spits an error on you when you try to mutate your state
        // either inside a dispatch or between dispatches. That doesn't work with Immutable.js
        // https://redux-toolkit.js.org/api/immutabilityMiddleware
        immutableCheck: false,
      }).concat([
        // this middleware is required to manage subscription lifetimes
        sweetAPI.middleware,
        // send request errors to Sentry
        ErrorLoggerMiddleware,
        // handle global errors (no-account, trial-expired, 2fa-required, etc)
        APIGlobalErrorHandlerMiddleware,
        // compare backend/app versions and show the reload message
        AppVersionCheckerMiddleware,
      ]),
  });

  // https://redux-toolkit.js.org/rtk-query/api/setupListeners
  setupListeners(store.dispatch);

  // save store data into localStorage each 2s
  const unsubscribe = store.subscribe(() =>
    throttledSaveStateToLocalStorage(store)
  );

  // func used by tests, to avoid leaking throttled calls from one test to another
  function stopLocalStorageSync() {
    unsubscribe();
    throttledSaveStateToLocalStorage.cancel();
  }

  return {
    store,
    stopLocalStorageSync,
  };
}

export type StoreType = ReturnType<typeof configureSweetStore>['store'];

export type AppDispatchType = StoreType['dispatch'];

export default configureSweetStore().store;
